// The job's front door (30-day list row 30, the project home from the critique): where the job stands at a
// glance and the next things in order. The shell reads no records here: every tile and every next thing comes
// from a module's own health function, declared in its manifest, and only the modules this company has
// and this role may open contribute. A module that is off is simply absent.
import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { STAGES } from '@tradeview/shared';
import { healthFor, type Health, type HealthNext } from '@tradeview/core';
import { Head, Empty, Icon } from '../sdk';
import { useAuth } from '../auth';
import { useRoom } from '../app/room';
import { rememberProject } from '../app/projects';
import { fmtDate } from '../sdk-model';

export function JobHome() {
  const { id = '' } = useParams();
  const { session } = useAuth();
  const { projects, loading } = useRoom();
  const p = projects.find(x => x.id === id) ?? null;
  const role = session?.role ?? null; const ent = session?.entitlements ?? null;
  const h = useQuery({
    queryKey: ['job-front-door', session?.paths.companyId, p && !p.bid ? p.id : null, role],
    enabled: !!session && !!p && !p.bid, staleTime: 60_000,
    queryFn: async (): Promise<Health> => {
      const ctx = { pid: p!.id, companyId: session!.paths.companyId, project: session!.paths.project, projectSub: session!.paths.projectSub };
      const parts = await Promise.all(healthFor(role, ent).map(async m => { try { const fn = await m.load(); return await fn(ctx); } catch { return { tiles: [], next: [] } as Health; } }));
      return { tiles: parts.flatMap(x => x.tiles), next: parts.flatMap(x => x.next) };
    },
  });
  // Landing here by link is entering the room.
  useEffect(() => { if (p) rememberProject(p.id); }, [p]);
  if (loading && !p) return <div className="skel h-40 rounded-[14px] mt-2" />;
  if (!p) return <Empty icon="home" title="No such job" text="It may have been archived, or it is a bid: bids open on the Bids page." action={<Link to="/" className="btn">Today</Link>} />;
  if (p.bid) return <Empty icon="flag" title={`${p.name} is a bid`} text="A bid's front door is its bid page." action={<Link to={`/bids/${p.id}`} className="btn btn-primary">Open the bid</Link>} />;
  const stage = STAGES[p.stage as keyof typeof STAGES]?.label ?? p.stage;
  const d = h.data;
  const next: HealthNext[] = d ? [...d.next].sort((a, b) => Number(b.warn) - Number(a.warn)) : [];
  const toneCls = (t?: string) => t === 'crit' ? 'text-crit' : t === 'warn' ? 'text-warn' : 'text-muted';
  return (
    <div className="flex flex-col gap-2.5 pb-24">
      <Head title={p.name} sub={[stage, p.startDate ? `started ${fmtDate(p.startDate)}` : '', p.targetEnd ? `finish ${fmtDate(p.targetEnd)}` : '', p.customerName || p.owner || '', p.gcPmName ? `GC PM ${p.gcPmName}` : '', p.address || ''].filter(Boolean).join(' · ')} />
      {h.isLoading && <div className="skel h-24 rounded-[14px]" />}
      {d && d.tiles.length > 0 && <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {d.tiles.map(t => <Link key={t.label} to={t.to} className="card py-2.5 min-w-0"><div className="lbl">{t.label}</div><div className="font-disp font-bold text-[20px] leading-none mt-1 truncate">{t.big}</div><div className={`text-[12px] mt-0.5 truncate ${toneCls(t.tone)}`}>{t.sub}</div></Link>)}
      </div>}
      {d && d.tiles.length === 0 && <p className="m-0 text-[13.5px] text-muted">None of the modules you can open has a word on this job yet.</p>}
      {d && <div className="card py-1">
        <h4 className="lbl m-0 mt-1.5 mb-1">Next things, in order</h4>
        {next.length === 0 && <p className="m-0 py-2 text-[13.5px] text-muted">Nothing waiting on anyone. The plan, the GC and the material are all where they should be.</p>}
        {next.map(x => <Link key={x.label} to={x.to} className="lrow"><span className={`w-2 h-2 rounded-full shrink-0 ${x.warn ? 'bg-accent' : 'bg-line'}`} /><div className="min-w-0 flex-1"><b className="block font-head font-bold text-[14px] truncate">{x.label}</b><small className="block text-[12.5px] text-muted truncate">{x.detail}</small></div><Icon name="chevron" size={16} className="text-muted shrink-0" /></Link>)}
      </div>}
      <p className="m-0 text-[12.5px] text-muted">Each tile is one module's word on this job and opens it there. A module the company does not have shows nothing here.</p>
    </div>
  );
}
